import { Chip, cn, Table } from '@heroui/react';
import { timeIn, ZONES, type ScheduleItem } from '../lib/schedule.ts';
import type { Occurrence } from '../data/api.ts';

/**
 * One day of the schedule, as a table.
 *
 * ⚠️ SHARED BY Расписание AND THE HOME PAGE, and that is the point of the file
 * rather than a convenience. The home page shows today and the schedule page
 * shows three weeks of days, but they are the same rows answering the same
 * question, and two copies would drift the moment either grew a column — the
 * member comparing them would be reading two different schedules.
 *
 * ONE table at every width, phone included, and the SAME table as the character
 * list on the home page — the default variant, framed in `--surface-secondary`.
 *
 * A day is read by running your eye DOWN a time column — «что раньше, что
 * позже» — and comparing two activities against each other. The card-per-row
 * phone layout this replaced kept each occurrence whole and destroyed exactly
 * that: two events an hour apart became two blocks you had to hold in your
 * head, because the times no longer shared an edge.
 *
 * ⚠️ IT IS SIZED TO FIT RATHER THAN TO SCROLL, which is the difference from the
 * ten-column tables on the admin pages. `Table.ScrollContainer` stays as the
 * backstop for a title with one unbreakable 40-character word in it; measured
 * at 320, 390 and 430 it has nothing to scroll and renders no scrollbar.
 *
 * The column count stays DERIVED from ZONES. It has already changed once
 * (DECISION-27 dropped Minsk) and a hardcoded three would put the header and
 * the cells out of step the next time it does.
 */

/**
 * A time column: a FIXED width, identical in every day's table.
 *
 * ⚠️ THIS WAS `w-0`, AND THE DAYS CAME OUT WITH DIFFERENT COLUMNS (owner's
 * report, 2026-08-12). Every day is its own `<table>`, and `w-0` only means
 * anything through `table-layout: auto` — which sizes columns from THAT table's
 * own content and shares out whatever is left over. So a day whose longest
 * description ran the full width put the times hard against the right edge,
 * while a day of three short rows floated them into the middle. Three cards,
 * three layouts, on a page you read by running your eye down a column.
 *
 * `table-fixed` plus an explicit width is what makes the answer depend on the
 * CSS and nothing else. 4.5rem is «21:35» in tabular text-sm (~38px) plus the
 * `px-2` either side, with room over; the activity column is left unspecified
 * and takes the remainder, so it still wraps rather than pushing anything off a
 * phone. Measured across every day's table at 320/390/768/1200: identical.
 *
 * ONE constant for the header and the cell. Under `table-fixed` only the FIRST
 * row is measured, so a width on the `<td>` alone would be ignored outright —
 * and the two silently disagreeing is worse than either being wrong.
 */
const TIME_CELL = 'w-[4.5rem] whitespace-nowrap px-2';

/** One occurrence is an event at an instant; neither alone is unique. */
const rowId = (occurrence: Occurrence) => `${occurrence.eventId}-${occurrence.at}`;

export function ScheduleTable({
  items,
  ariaLabel,
}: {
  items: ScheduleItem<Occurrence>[];
  ariaLabel: string;
}) {
  return (
    <Table>
      <Table.ScrollContainer>
        {/* `table-fixed` — see TIME_CELL. Without it each day's card computes
            its own column widths from its own rows, and the cards on the page
            stop lining up with one another. */}
        <Table.Content aria-label={ariaLabel} className="table-fixed">
          <Table.Header>
            <Table.Column id="activity" isRowHeader>
              Активность
            </Table.Column>
            {ZONES.map((zone) => (
              <Table.Column key={zone.id} id={zone.id} className={TIME_CELL}>
                {zone.short}
              </Table.Column>
            ))}
          </Table.Header>
          <Table.Body>
            {items.map(({ occurrence, isOngoing, isPast }) => (
              <Table.Row
                key={rowId(occurrence)}
                id={rowId(occurrence)}
                /*
                  What is happening right now, marked exactly as your own
                  character is marked on the home page — same token, same
                  reason: one row in a list you are scanning for a single
                  answer.

                  Painted on the CELLS, not the row: HeroUI puts the background
                  on every `<td>`, so a background on the `<tr>` sits behind
                  them and shows nothing.
                */
                className={cn(isOngoing && '[&>td]:bg-accent-soft')}
              >
                {/* Everything already over is dimmed as a whole, so the eye
                    skips it. Applied to the CELL rather than to the title
                    alone: the times are most of what a finished row still
                    says, and half a row greyed reads as a rendering fault. */}
                <Table.Cell className={cn(isPast && 'text-muted')}>
                  {/* `flex-wrap`: at 320px the title plus the chip exceed the
                      fixed activity column, and without it the chip is simply
                      clipped. Dropping to a second line costs a row of height
                      on the smallest phones and nothing anywhere else. */}
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className={cn('font-medium', isPast && 'font-normal')}>
                      {occurrence.title}
                    </span>
                    {/* The highlight said in words as well as in colour. A
                        background alone is not a signal to a reader who cannot
                        see it, and «вы» on the home page sets the precedent. */}
                    {isOngoing && (
                      <Chip size="sm" variant="soft" color="accent" className="shrink-0">
                        идёт
                      </Chip>
                    )}
                  </div>
                  {occurrence.description && (
                    <span className="block text-xs text-muted">{occurrence.description}</span>
                  )}
                </Table.Cell>
                {/* No `tabular` on these: the base layer already gives every
                    th/td tabular figures. */}
                {ZONES.map((zone) => (
                  <Table.Cell key={zone.id} className={cn(TIME_CELL, isPast && 'text-muted')}>
                    {timeIn(occurrence.at, zone.id)}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
    </Table>
  );
}
